import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'

import { AnamnesisStep, AnamnesisStepName } from '../@types/Patient'
import { buildFlow, DOCUMENT_PATH_PREFIX, FlowStep, stepNameToPathSegment } from '../config/flow'
import { paths } from '../config/routes'

import { usePatient } from './PatientProvider'

const ANAMNESIS_INTERNAL_PATHS: ReadonlySet<string> = new Set([
  paths.personalInformation,
  paths.address,
  paths.insurance,
  paths.preConditions1,
  paths.preConditions2,
  paths.preConditions3,
  paths.allergies,
  paths.preparations,
  paths.wishes,
  paths.wishes2,
  paths.wishes3,
  paths.referral,
  paths.consents,
  paths.summary,
])

const isAnamnesisFormPath = (pathname: string): boolean => ANAMNESIS_INTERNAL_PATHS.has(pathname)

const isDocumentPath = (pathname: string): boolean => pathname.startsWith(`${DOCUMENT_PATH_PREFIX}/`)

const documentPathToStepName = (pathname: string, flow: FlowStep[]): string | null => {
  const segment = pathname.slice(DOCUMENT_PATH_PREFIX.length + 1)
  const match = flow.find(({ step }) => stepNameToPathSegment(step.step) === segment)

  return match?.step.step ?? null
}

const findNextEntryPath = (
  flow: FlowStep[],
  currentStepName: string | null,
  completed: ReadonlySet<string>
): string | null => {
  const startIndex = currentStepName ? flow.findIndex(({ step }) => step.step === currentStepName) + 1 : 0

  for (let i = startIndex; i < flow.length; i++) {
    if (!completed.has(flow[i].step.step)) return flow[i].entryPath
  }

  return null
}

export type FlowEngineContextType = {
  flow: FlowStep[]
  /**
   * Resolve which top-level step (by name) owns the given pathname.
   * Returns null when the pathname is outside the flow (e.g., /intro, /success).
   */
  resolveStepName: (pathname: string) => string | null
  /**
   * Returns the entry path for the next renderable step that has not been
   * locally completed. Pass `null` to get the first step's path. Returns null
   * when the flow is exhausted. Reads from the current snapshot — does not
   * refresh.
   */
  getNextEntryPath: (currentStepName: string | null) => string | null
  /**
   * Returns the entry path for the previous renderable step. Returns null
   * when there is no previous step.
   */
  getPreviousEntryPath: (currentStepName: string) => string | null
  /**
   * Mark a step as locally complete, refetch the patient so step prerequisites
   * (e.g. insurance type) are re-evaluated, and return the entry path of the
   * next renderable step. Returns null when the flow is exhausted.
   */
  advance: (currentStepName: string) => Promise<string | null>
  /** True when every step in the flow is locally complete. */
  isFlowComplete: () => boolean
}

const FlowEngineContext = createContext<FlowEngineContextType | undefined>(undefined)

type Snapshot = {
  patientReferenceId: string
  steps: AnamnesisStep[]
}

const FlowEngineProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const patient = usePatient()
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null)
  const [completedSteps, setCompletedSteps] = useState<ReadonlySet<string>>(() => new Set())

  const patientReferenceId = patient?.data?.referenceId
  const anamnesisState = patient?.data?.anamnesisState

  useEffect(() => {
    if (!patientReferenceId || !anamnesisState) return

    if (snapshot?.patientReferenceId !== patientReferenceId) {
      setSnapshot({ patientReferenceId, steps: anamnesisState })
      setCompletedSteps(new Set())
    }
  }, [patientReferenceId, anamnesisState, snapshot?.patientReferenceId])

  const flow = useMemo<FlowStep[]>(() => buildFlow(snapshot?.steps), [snapshot])

  const resolveStepName = useCallback(
    (pathname: string): string | null => {
      if (isAnamnesisFormPath(pathname)) return AnamnesisStepName.ANAMNESIS

      if (pathname === paths.factoringAgreement) return AnamnesisStepName.FACTORING_AGREEMENT

      if (isDocumentPath(pathname)) return documentPathToStepName(pathname, flow)

      return null
    },
    [flow]
  )

  const getNextEntryPath = useCallback(
    (currentStepName: string | null): string | null => findNextEntryPath(flow, currentStepName, completedSteps),
    [flow, completedSteps]
  )

  const getPreviousEntryPath = useCallback(
    (currentStepName: string): string | null => {
      const currentIndex = flow.findIndex(({ step }) => step.step === currentStepName)

      if (currentIndex <= 0) return null

      for (let i = currentIndex - 1; i >= 0; i--) {
        if (!completedSteps.has(flow[i].step.step)) return flow[i].entryPath
      }

      return null
    },
    [flow, completedSteps]
  )

  const advance = useCallback(
    async (currentStepName: string): Promise<string | null> => {
      const updatedCompleted = new Set(completedSteps)
      updatedCompleted.add(currentStepName)
      setCompletedSteps(updatedCompleted)

      let freshSteps: AnamnesisStep[] | undefined = snapshot?.steps

      if (patient?.refetch) {
        try {
          const result = await patient.refetch()

          if (result.data?.anamnesisState) {
            freshSteps = result.data.anamnesisState

            if (patientReferenceId) {
              setSnapshot({ patientReferenceId, steps: freshSteps })
            }
          }
        } catch {
          // Refetch failed — fall back to the existing snapshot so navigation still works.
        }
      }

      return findNextEntryPath(buildFlow(freshSteps), currentStepName, updatedCompleted)
    },
    [completedSteps, patient, patientReferenceId, snapshot?.steps]
  )

  const isFlowComplete = useCallback(
    () => flow.every(({ step }) => completedSteps.has(step.step)),
    [flow, completedSteps]
  )

  const value = useMemo<FlowEngineContextType>(
    () => ({
      flow,
      resolveStepName,
      getNextEntryPath,
      getPreviousEntryPath,
      advance,
      isFlowComplete,
    }),
    [flow, resolveStepName, getNextEntryPath, getPreviousEntryPath, advance, isFlowComplete]
  )

  return <FlowEngineContext.Provider value={value}>{children}</FlowEngineContext.Provider>
}

export const useFlowEngine = (): FlowEngineContextType => {
  const context = useContext(FlowEngineContext)

  if (context === undefined) {
    throw new Error('useFlowEngine must be used within FlowEngineProvider.')
  }

  return context
}

export default FlowEngineProvider
